import { z } from 'zod';
// Subpath ./schemas của @apv-erp/trpc trỏ về nguồn ESM (giống @apv-erp/status) -- bản CJS dist không bundle được named export
import { createCustomerSchema } from '@apv-erp/trpc/schemas';
import type { CustomerRow } from '@apv-erp/status/generated';

const optStr = (max = 200) => z.preprocess(
    (v) => (v === null || v === undefined ? undefined : v),
    z.string().max(max).optional().or(z.literal(''))
);

// Regex: SĐT Việt Nam (10 số, đầu 03x/05x/07x/08x/09x) hoặc cố định (02x) hoặc quốc tế (+…)
export const PHONE_REGEX = /^(\+[1-9]\d{6,14}|0[35789]\d{8}|0[2][0-9]\d{7,8})$/;

export const validatePhoneNumber = (v: string | undefined | null) => {
    if (!v) return 'Số điện thoại là bắt buộc';
    const cleaned = v.replace(/[\s\-().]/g, '');
    if (!PHONE_REGEX.test(cleaned)) {
        return 'SĐT không hợp lệ (Di động: 10 số, Bàn: 10-11 số, Quốc tế: +xxx)';
    }
    return undefined;
};

export const optPhone = (label: string) =>
    z.string().max(20).refine(
        (v) => !v || PHONE_REGEX.test(v.replace(/[\s\-().]/g, '')),
        { message: `${label} không hợp lệ. VD: 0901234567 (VN) hoặc +84901234567 (Quốc tế)` }
    ).optional().or(z.literal(''));

/** Generate customer code from Vietnamese short name (VD: "ANPHA II" -> "ANII", "APV3050" -> "APV3050") */
export function generateCustomerCode(shortName: string): string {
    if (!shortName) return '';
    // Remove Vietnamese diacritics
    const normalized = shortName.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/đ/gi, 'd');
    let cleaned = normalized.toUpperCase().replace(/[^A-Z0-9\s]/g, '').trim();
    const words = cleaned.split(/\s+/).filter(Boolean);
    if (words.length === 0) return '';
    if (words.length === 1) {
        return words[0].length <= 10 ? words[0] : words[0].substring(0, 8);
    }
    if (words.length === 2) return (words[0].substring(0, 3) + words[1].substring(0, 3));
    if (words.length === 3) return (words[0].substring(0, 2) + words[1].substring(0, 2) + words[2].substring(0, 2));
    // 4+ words: first letter of each, take first 6
    return words.slice(0, 6).map(w => w[0]).join('');
}

/**
 * Trích xuất tên thương hiệu cốt lõi từ tên đầy đủ pháp nhân doanh nghiệp
 * VD:
 * - "CÔNG TY TNHH CÔNG NGHIỆP CASA FIORE VIỆT NAM" -> "CASA FIORE"
 * - "CÔNG TY TNHH MTV THƯƠNG MẠI SẢN XUẤT ANPHA II" -> "ANPHA II"
 * - "CÔNG TY CỔ PHẦN BAO BÌ ART PRESTO" -> "ART PRESTO"
 */
export function extractSmartBrandName(fullName: string): string {
    if (!fullName) return '';
    let name = fullName.trim();

    // 1. Loại bỏ các tiền tố pháp nhân phổ biến
    const LEGAL_PREFIXES = [
        /^CÔNG TY TNHH MỘT THÀNH VIÊN\s+/i,
        /^CÔNG TY TNHH MTV\s+/i,
        /^CÔNG TY TNHH\s+/i,
        /^CÔNG TY CỔ PHẦN\s+/i,
        /^CÔNG TY CP\s+/i,
        /^CÔNG TY HỢP DANH\s+/i,
        /^DOANH NGHIỆP TƯ NHÂN\s+/i,
        /^DNTN\s+/i,
        /^TẬP ĐOÀN\s+/i,
        /^CHI NHÁNH CÔNG TY TNHH MTV\s+/i,
        /^CHI NHÁNH CÔNG TY TNHH\s+/i,
        /^CHI NHÁNH CÔNG TY CỔ PHẦN\s+/i,
        /^CHI NHÁNH\s+/i,
        /^VĂN PHÒNG ĐẠI DIỆN\s+/i,
    ];

    for (const prefix of LEGAL_PREFIXES) {
        if (prefix.test(name)) {
            name = name.replace(prefix, '').trim();
            break;
        }
    }

    // 2. Loại bỏ các tiền tố ngành nghề phổ biến nếu đứng đầu tên riêng
    const INDUSTRY_PREFIXES = [
        /^SẢN XUẤT THƯƠNG MẠI DỊCH VỤ\s+/i,
        /^SẢN XUẤT THƯƠNG MẠI\s+/i,
        /^THƯƠNG MẠI DỊCH VỤ\s+/i,
        /^THƯƠNG MẠI VÀ DỊCH VỤ\s+/i,
        /^THƯƠNG MẠI\s+/i,
        /^SẢN XUẤT\s+/i,
        /^CÔNG NGHIỆP\s+/i,
        /^ĐẦU TƯ VÀ PHÁT TRIỂN\s+/i,
        /^ĐẦU TƯ THƯƠNG MẠI\s+/i,
        /^ĐẦU TƯ\s+/i,
        /^XÂY DỰNG\s+/i,
        /^DỊCH VỤ\s+/i,
        /^IN VÀ BAO BÌ\s+/i,
        /^IN BAO BÌ\s+/i,
        /^BAO BÌ\s+/i,
    ];

    for (const prefix of INDUSTRY_PREFIXES) {
        if (prefix.test(name)) {
            const candidate = name.replace(prefix, '').trim();
            if (candidate && candidate.length >= 2) {
                name = candidate;
                break;
            }
        }
    }

    // 3. Loại bỏ hậu tố quốc gia nếu đứng cuối và tên có >= 3 từ
    const words = name.split(/\s+/).filter(Boolean);
    if (words.length >= 3) {
        if (/^(VIỆT\s+NAM|VN|VIETNAM)$/i.test(words.slice(-2).join(' '))) {
            name = words.slice(0, -2).join(' ');
        } else if (/^(VIETNAM|VN)$/i.test(words[words.length - 1])) {
            name = words.slice(0, -1).join(' ');
        }
    }

    return name.trim();
}

/** Tra cứu thông tin doanh nghiệp qua API VietQR bằng Mã số thuế */
export async function lookupBusinessByTaxCode(taxCode: string): Promise<{
    name?: string;
    address?: string;
    shortName?: string;
    internationalName?: string;
    status?: string;
} | null> {
    let cleanTax = (taxCode || '').replace(/[^\d-]/g, '').trim();
    if (cleanTax.length === 13 && !cleanTax.includes('-')) {
        cleanTax = `${cleanTax.slice(0, 10)}-${cleanTax.slice(10)}`;
    }
    if (!cleanTax || (cleanTax.length !== 10 && cleanTax.length !== 14)) return null;
    try {
        const res = await fetch(`https://api.vietqr.io/v2/business/${cleanTax}`);
        if (!res.ok) return null;
        const json = await res.json();
        if (json.code === '00' && json.data) {
            const fullName = (json.data.name || '').trim();
            const rawShort = (json.data.shortName || json.data.displayName || '').trim();
            const smartShort = rawShort || extractSmartBrandName(fullName);

            return {
                name: fullName,
                address: (json.data.address || '').trim(),
                shortName: smartShort,
                internationalName: (json.data.internationalName || '').trim(),
                status: json.data.status || '',
            };
        }
    } catch {
        // Fallback silently if offline
    }
    return null;
}

// Form schema — dùng chung cho Create + Edit (Rule 2 Canonical Form)
// Luồng đồng nhất: DB (Prisma) -> createCustomerSchema (backend, @apv-erp/trpc) -> schema form này.
// Form được phép CHẶT HƠN API (bắt buộc shortName/phone/address, giới hạn độ dài, chuẩn hóa null -> ''),
// nhưng KHÔNG được thêm field mà API không nhận hoặc sinh ra kiểu API từ chối (xem assert cuối file).
export const customerFormSchema = createCustomerSchema.extend({
    // — Thông tin cơ bản —
    code: z.string().min(1, 'Mã KH không được để trống').max(20, 'Tối đa 20 ký tự'),
    name: z.string().min(1, 'Tên đầy đủ không được để trống').max(200, 'Tối đa 200 ký tự'),
    shortName: z.string().min(1, 'Tên viết tắt không được để trống').max(50, 'Tối đa 50 ký tự'),
    type: optStr(50).default('COMPANY'),
    group: optStr(50),
    representative: optStr(100),
    position: optStr(100),
    taxCode: optStr(20),
    website: optStr(200),
    fanpage: optStr(500),
    logo: optStr(1000),

    // — Liên hệ & Đại diện tác nghiệp —
    phone: z.string().min(1, 'Số điện thoại là bắt buộc').max(20).refine(
        (v) => !v || PHONE_REGEX.test(v.replace(/[\s\-().]/g, '')),
        { message: 'SĐT không hợp lệ (Di động: 10 số, Bàn: 10-11 số, Quốc tế: +xxx)' }
    ),
    contactPerson: optStr(100),
    mobile: optPhone('Số điện thoại liên hệ'),
    email: z.string().email('Email không hợp lệ (VD: info@company.vn)').optional().or(z.literal('')),

    // — Địa chỉ & Vận chuyển —
    address: z.string().min(1, 'Địa chỉ không được để trống').max(500, 'Tối đa 500 ký tự'),
    deliveryAddress: optStr(500),
    deliveryMethod: optStr(100),
    transitAddress: optStr(500),

    // — Ngân hàng & Công nợ —
    bankName: optStr(200),
    bankAccountNumber: optStr(50),
    bankAccountHolder: optStr(200),
    creditLimit: z.preprocess((v) => (v === '' || v === null ? 0 : v), z.coerce.number().min(0).default(0)),
    initialDebt: z.preprocess((v) => (v === '' || v === null ? 0 : v), z.coerce.number().min(0).default(0)),
    paymentTerms: optStr(500),

    // — Tiêu chuẩn in ấn & Dung sai —
    colorTolerance: optStr(200),
    quantityTolerance: optStr(200),
    tolerancePercent: z.preprocess((v) => (v === '' || v === null ? 1.0 : v), z.coerce.number().min(0).max(10).default(1.0)),

    // — Quản trị, Ghi chú & Phân loại —
    salesUserId: optStr(50).nullable(),
    notes: optStr(1000),
    tags: optStr(500),
    status: z.enum(['ACTIVE', 'INACTIVE']).default('ACTIVE'),
});

export type CustomerFormData = z.infer<typeof customerFormSchema>;

// ── Ràng buộc đồng nhất lúc biên dịch ──
// Nếu API đổi/xóa field hoặc form sinh dữ liệu API không nhận, tsc sẽ báo lỗi ở đây.
type AssertTrue<T extends true> = T;
/** Dữ liệu form (sau validate) luôn gửi được vào customers.create */
export type _CustomerFormFitsApiInput = AssertTrue<
    z.output<typeof customerFormSchema> extends z.input<typeof createCustomerSchema> ? true : false
>;
/** Form không có field nào ngoài API */
export type _CustomerFormHasNoExtraKeys = AssertTrue<
    Exclude<keyof CustomerFormData, keyof z.input<typeof createCustomerSchema>> extends never ? true : false
>;

// Interface đầy đủ (bao gồm audit + computed fields)
export interface Customer {
    id: string;
    code: string;
    name: string;
    shortName?: string | null;
    type?: string | null;
    group?: string | null;
    representative?: string | null;
    address?: string | null;
    deliveryMethod?: string | null;
    deliveryAddress?: string | null;
    transitAddress?: string | null;
    taxCode?: string | null;
    contactPerson?: string | null;
    phone?: string | null;
    mobile?: string | null;
    email?: string | null;
    website?: string | null;
    fanpage?: string | null;
    logo?: string | null;
    creditLimit?: number | null;
    bankName?: string | null;
    bankAccountNumber?: string | null;
    bankAccountHolder?: string | null;
    paymentTerms?: string | null;
    position?: string | null;
    colorTolerance?: string | null;
    quantityTolerance?: string | null;
    tolerancePercent?: number | null;
    salesUserId?: string | null;
    salesUser?: { id: string; fullName: string; } | null;
    salesUsers?: { id: string; user?: { id: string; fullName: string }; department?: string }[];
    notes?: string | null;
    tags?: string | null;
    panel?: string | null;
    initialDebt?: number;
    currentDebt?: number;
    openingDebt?: number;
    orderValue?: number;
    deliveryValue?: number;
    paidAmount?: number;
    remainingDebt?: number;
    status: 'ACTIVE' | 'INACTIVE';
    createdBy?: string | null;
    createdAt: string;
    updatedAt: string;
}

/** Mọi field của interface Customer (trừ quan hệ) phải là cột có thật trong DB (prisma/schema.prisma) */
export type _CustomerInterfaceMatchesDb = AssertTrue<
    Exclude<keyof Customer, keyof CustomerRow | 'salesUser' | 'salesUsers'> extends never ? true : false
>;
